import { useState } from 'react';
import { ChevronDown, Filter } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type {
  PipelineFunnelBucket,
  PipelineFunnelCandidate,
  PipelineFunnelStage,
} from '@hygge/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { usePipelineFunnel } from '@/hooks/use-dashboard';
import { WidgetCard } from '../widget-card';
import { ExpandableList } from '../expandable-list';
import { Chip, type ChipColor } from '../chip';
import { asArray } from '../widget-format';

/** Funnel stage → label + count-chip color. */
const STAGE_META: Record<PipelineFunnelStage, { label: string; color: ChipColor }> = {
  new: { label: 'New / Sourcing', color: 'gray' },
  active: { label: 'Active', color: 'blue' },
  hiring: { label: 'Hiring', color: 'amber' },
  hired: { label: 'Hired', color: 'green' },
};

function CandidateRow({ c }: { c: PipelineFunnelCandidate }) {
  return (
    <Link
      to="/recruitment/candidates/$id"
      params={{ id: c.candidateId }}
      className="dashboard-row group flex items-center gap-3 border-b border-border py-2 last:border-0"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm group-hover:underline">{c.candidateName}</div>
        <div className="truncate text-xs text-muted-foreground">{c.vacancy}</div>
      </div>
    </Link>
  );
}

/**
 * A bucket's size: the count the server sent, or failing that the rows it sent with it — a
 * missing count must not collapse a stage that has candidates into a disabled, unopenable row.
 */
function bucketCount(bucket: PipelineFunnelBucket): number {
  const size = Number(bucket.count ?? asArray(bucket.candidates).length);
  return Number.isFinite(size) ? size : 0;
}

function StageBlock({ bucket }: { bucket: PipelineFunnelBucket }) {
  const [open, setOpen] = useState(false);
  // A bucket the frontend has no entry for — a stage added server-side — is drawn under its
  // own key rather than crashing the card, and one that arrives without a stage at all gets a
  // neutral label, so no row is left as a bare count chip. `Object.hasOwn`, because a stage
  // named like an inherited member (`toString`) would otherwise resolve to a function.
  const meta = Object.hasOwn(STAGE_META, bucket.stage)
    ? STAGE_META[bucket.stage]
    : { label: bucket.stage || 'Other', color: 'gray' as ChipColor };
  const count = bucketCount(bucket);
  const empty = count === 0;

  return (
    <div className="border-b border-border py-1.5 last:border-0">
      <button
        type="button"
        disabled={empty}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-2 py-1 text-left text-sm',
          empty ? 'cursor-default text-muted-foreground' : 'cursor-pointer',
        )}
      >
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
            empty && 'opacity-0',
          )}
        />
        <span className="flex-1 font-medium">{meta.label}</span>
        <Chip color={meta.color}>{count}</Chip>
      </button>
      {open && !empty && (
        <div className="pl-6">
          <ExpandableList
            items={bucket.candidates}
            getKey={(c) => c.id}
            renderItem={(c: PipelineFunnelCandidate) => <CandidateRow c={c} />}
          />
        </div>
      )}
    </div>
  );
}

/**
 * `pipelineFunnel` widget — the caller's candidates as a funnel grouped by lifecycle
 * bucket (New/Sourcing → Active → Hiring → Hired). Each stage expands to its
 * candidate list; clicking a candidate opens their page.
 */
export function PipelineFunnelWidget() {
  const { data, isLoading, isError } = usePipelineFunnel();
  const buckets = asArray(data);
  const total = buckets.reduce((sum, b) => sum + bucketCount(b), 0);

  return (
    <WidgetCard
      title="Pipeline Funnel"
      icon={Filter}
      caption="Your candidates by stage · summed across your vacancies."
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-5 w-8" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Couldn&apos;t load the funnel.
        </p>
      ) : total === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">No candidates yet.</p>
      ) : (
        <div>
          {buckets.map((b, i) => (
            <StageBlock key={b.stage || `unknown-${i}`} bucket={b} />
          ))}
        </div>
      )}
    </WidgetCard>
  );
}
